export type ApiErrorDetail = {
  field: string;
  message: string;
};

type ApiErrorBody = {
  error: {
    code: string;
    message: string;
    details?: ApiErrorDetail[];
    request_id?: string;
  };
};

export class ApiError extends Error {
  readonly code: string;
  readonly details: ApiErrorDetail[];
  readonly requestId: string | undefined;
  readonly status: number;

  constructor(params: {
    code: string;
    message: string;
    details?: ApiErrorDetail[];
    requestId?: string;
    status: number;
  }) {
    super(params.message);
    this.name = "ApiError";
    this.code = params.code;
    this.details = params.details ?? [];
    this.requestId = params.requestId;
    this.status = params.status;
  }
}

const configuredBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL ?? "/api/v1";
const apiBaseUrl = configuredBaseUrl.replace(/\/$/, "");

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== "object" || value === null || !("error" in value)) {
    return false;
  }

  const error = value.error;

  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    typeof error.code === "string" &&
    "message" in error &&
    typeof error.message === "string"
  );
}

export type UploadProgress = { loaded: number; total: number };

// XHR exposes actual upload progress; keep authentication and error parsing shared.
function uploadResponse(url: string, init: RequestInit, onProgress: (progress: UploadProgress) => void, onServerProgress?: (event: unknown) => void): Promise<Response> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(init.method ?? "POST", url);
    xhr.withCredentials = true;
    xhr.timeout = 360_000;
    new Headers(init.headers).forEach((value, key) => xhr.setRequestHeader(key, value));
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress({ loaded: event.loaded, total: event.total });
    };
    let offset = 0;
    let terminal: { status: number; body: string } | undefined;
    const readEvents = () => {
      if (!onServerProgress || xhr.status !== 200) return;
      let end: number;
      while ((end = xhr.responseText.indexOf("\n", offset)) !== -1) {
        const line = xhr.responseText.slice(offset, end);
        offset = end + 1;
        if (!line.trim()) continue;
        const event: unknown = JSON.parse(line);
        if (typeof event !== "object" || event === null || !("type" in event) || terminal) continue;
        if (event.type === "progress") onServerProgress(event);
        if (event.type === "result" && "data" in event) terminal = { status: 200, body: JSON.stringify(event.data) };
        if (event.type === "error" && isApiErrorBody(event)) terminal = { status: "status" in event && typeof event.status === "number" ? event.status : 500, body: JSON.stringify(event) };
      }
    };
    const invalidStream = () => reject(new ApiError({ code: "UPLOAD_INCOMPLETE", status: 0, message: "A confirmação do processamento foi interrompida. Confira a biblioteca antes de tentar novamente." }));
    xhr.onprogress = () => { try { readEvents(); } catch { invalidStream(); xhr.abort(); } };
    xhr.onload = () => {
      try {
        readEvents();
        if (onServerProgress && xhr.status === 200 && !terminal) { invalidStream(); return; }
        resolve(new Response(terminal ? terminal.body : xhr.status === 204 ? null : xhr.responseText, { status: terminal?.status ?? xhr.status, statusText: xhr.statusText }));
      } catch { invalidStream(); }
    };
    xhr.onerror = () => reject(new ApiError({ code: "NETWORK_ERROR", status: 0, message: "A conexão foi interrompida. Confira a biblioteca antes de tentar novamente." }));
    xhr.ontimeout = () => reject(new ApiError({ code: "UPLOAD_TIMEOUT", status: 0, message: "O envio demorou mais que o esperado. Confira a biblioteca antes de tentar novamente." }));
    xhr.onabort = () => reject(new DOMException("Envio cancelado.", "AbortError"));
    const abort = () => xhr.abort();
    xhr.onloadend = () => init.signal?.removeEventListener("abort", abort);
    if (init.signal?.aborted) { reject(new DOMException("Envio cancelado.", "AbortError")); return; }
    init.signal?.addEventListener("abort", abort, { once: true });
    xhr.send(init.body as XMLHttpRequestBodyInit | null | undefined);
  });
}

export async function apiFetch<T>(path: string, init: RequestInit = {}, onUploadProgress?: (progress: UploadProgress) => void, onServerProgress?: (event: unknown) => void): Promise<T> {
  const url = `${apiBaseUrl}/${path.replace(/^\//, "")}`;
  const response = onUploadProgress ? await uploadResponse(url, init, onUploadProgress, onServerProgress) : await fetch(url, {
    ...init,
    credentials: "include",
  });

  if (!response.ok) {
    const body: unknown = await response.json().catch(() => undefined);

    if (isApiErrorBody(body)) {
      throw new ApiError({
        code: body.error.code,
        message: body.error.message,
        details: body.error.details,
        requestId: body.error.request_id,
        status: response.status,
      });
    }

    throw new ApiError({
      code: "HTTP_ERROR",
      message: onUploadProgress && response.status === 504 ? "O servidor demorou para processar o arquivo. Confira a biblioteca antes de tentar novamente." : onUploadProgress && response.status === 413 ? "O arquivo excedeu o limite de upload. Envie até 20 MB por figurinha." : response.statusText || "Não foi possível concluir a solicitação.",
      status: response.status,
    });
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}
